"use client";

import React, { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import OperatorsTableActionBar from "./OperatorsTableActionBar";
import {
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import OperatorsTableInfoModal from "./OperatorTableInfoModal";
import CustomChip from "../CustomChip/CustomChip";
import useFetchWithAuth from "../../features/hooks/useFetchWithAuth";
import StyledTableCell from "../ui/TableCell/TableCell";

const headers = [
  "Дата",
  "Годин",
  "Прийн.",
  "З допом",
  "Кросів",
  "Сума доп.П",
  "% доп.П",
  "Сер Чек",
  "Лідів/год",
  "Зароблено",
];

const options = [
  { value: "date", label: "Дата" },
  { value: "hours", label: "Годин" },
  { value: "received", label: "Прийн." },
  { value: "withHelp", label: "З допом" },
  { value: "crosses", label: "Кросів" },
  { value: "bonusSum", label: "Сума доп.П" },
  { value: "bonusPercent", label: "% доп.П" },
  { value: "avgCheck", label: "Сер Чек" },
  { value: "leadsPerHour", label: "Лідів/год" },
  { value: "earned", label: "Зароблено" },
];

function getKpiColor(type, value) {
  switch (type) {
    case "upsell_percent":
      if (value >= 72) return "lightgreen";
      if (value >= 60) return "khaki";
      return "lightcoral";
    case "avg_check":
      if (value >= 140) return "lightgreen";
      if (value >= 100) return "khaki";
      if (value < 90) return "lightcoral";
      return "khaki";
    case "leads_per_hour":
      if (value >= 5.8) return "lightgreen";
      if (value >= 4) return "khaki";
      return "lightcoral";
    default:
      return "transparent";
  }
}

export default function OperatorTableContainer() {
  const { operatorId } = useParams();
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [modalType, setModalType] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const modalRef = useRef(null);
  const [visibleColumns, setVisibleColumns] = useState(headers);
  const dateForRequest = useMemo(
    () => new Date().toISOString().slice(0, 10),
    []
  );

  const url = operatorId
    ? `/statistic/operator/ratings/?date_from=${dateForRequest}&date_to=${dateForRequest}`
    : null;

  const { data: apiResponse, loading, error } = useFetchWithAuth(url);
  const operatorData = useMemo(() => {
    const list = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
    const targetId = Number(operatorId);
    const row = list.find((item) => {
      const idValue = Number(item?.id ?? item?.operator_id);
      return Number.isFinite(idValue) && idValue === targetId;
    });
    if (!row) {
      return [];
    }
    return [
      {
        operator_id: row.id ?? row.operator_id ?? targetId,
        date: row.date ?? dateForRequest,
        hours: row.hours_worked ?? row.work_time ?? 0,
        received: row.accepted_count ?? row.accepted ?? 0,
        withHelp: row.orders_with_upsell ?? row.withHelp ?? 0,
        crosses: row.cross_count ?? row.cross ?? 0,
        bonusSum: row.upsell_margin_sum ?? row.totla_upsell ?? 0,
        bonusPercent: row.upsell_percent ?? row.percent_upsell ?? 0,
        avgCheck: row.avg_check ?? row.average_check ?? 0,
        leadsPerHour:
          row.leads_per_hour ?? row.orders_per_hour ?? row.leadsPerHour ?? 0,
        earned: row.operator_salary ?? row.salary_amount ?? row.salary ?? 0,
      },
    ];
  }, [apiResponse, operatorId, dateForRequest]);

  const handleSortOrderClick = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handleConfigClick = () => {
    setModalType("config");
    setOpenModal(true);
  };

  const sortedOperatorData = [...operatorData].sort((a, b) => {
    if (!sortBy) return 0;
    const valA = a[sortBy] ?? 0;
    const valB = b[sortBy] ?? 0;

    if (typeof valA === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    } else {
      return sortOrder === "asc" ? valA - valB : valB - valA;
    }
  });

  return (
    <>
      <Card sx={{ p: 2 }}>
        <OperatorsTableActionBar
          title="Список операторів"
          options={options}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          sortOrderLabel={sortOrder === "asc" ? "Зростання" : "Спадання"}
          onSortOrderClick={handleSortOrderClick}
          onConfigClick={handleConfigClick}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {headers
                  .filter((header) => visibleColumns.includes(header))
                  .map((header, idx) => (
                    <TableCell className="text-black border-bottom" key={idx}>
                      {header}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={headers.length}>
                    Завантаження...
                  </TableCell>
                </TableRow>
              )}
              {error && (
                <TableRow>
                  <TableCell colSpan={headers.length}>
                    Помилка завантаження даних
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                !error &&
                sortedOperatorData.map((op, idx) => (
                  <TableRow
                    key={op.operator_id ?? idx}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {headers
                      .filter((header) => visibleColumns.includes(header))
                      .map((header, i) => {
                        switch (header) {
                          case "Дата":
                            return (
                              <StyledTableCell key={i}>
                                <Typography variant="subtitle1" color="#64b5f6">
                                  {op.date ?? "-"}
                                </Typography>
                              </StyledTableCell>
                            );
                          case "Годин":
                            return (
                              <StyledTableCell key={i}>
                                {op.hours ?? "-"}
                              </StyledTableCell>
                            );
                          case "Прийн.":
                            return (
                              <StyledTableCell key={i}>
                                {op.received ?? "-"}
                              </StyledTableCell>
                            );
                          case "З допом":
                            return (
                              <StyledTableCell key={i}>
                                {op.withHelp ?? "-"}
                              </StyledTableCell>
                            );
                          case "Кросів":
                            return (
                              <StyledTableCell key={i}>
                                {op.crosses ?? "-"}
                              </StyledTableCell>
                            );
                          case "Сума доп.П":
                            return (
                              <StyledTableCell key={i}>
                                {op.bonusSum ?? "-"}
                              </StyledTableCell>
                            );
                          case "% доп.П":
                            return (
                              <StyledTableCell key={i}>
                                <CustomChip
                                  value={`${op.bonusPercent ?? 0}%`}
                                  color={getKpiColor(
                                    "upsell_percent",
                                    op.bonusPercent ?? 0
                                  )}
                                />
                              </StyledTableCell>
                            );
                          case "Сер Чек":
                            return (
                              <StyledTableCell key={i}>
                                <CustomChip
                                  value={op.avgCheck ?? 0}
                                  color={getKpiColor(
                                    "avg_check",
                                    op.avgCheck ?? 0
                                  )}
                                />
                              </StyledTableCell>
                            );
                          case "Лідів/год":
                            return (
                              <StyledTableCell key={i}>
                                <CustomChip
                                  value={op.leadsPerHour ?? 0}
                                  color={getKpiColor(
                                    "leads_per_hour",
                                    op.leadsPerHour ?? 0
                                  )}
                                />
                              </StyledTableCell>
                            );
                          case "Зароблено":
                            return (
                              <StyledTableCell key={i}>
                                {op.earned ?? "-"}
                              </StyledTableCell>
                            );
                          default:
                            return null;
                        }
                      })}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <OperatorsTableInfoModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        modalType={modalType}
        modalRef={modalRef}
        headers={headers}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
      />
    </>
  );
}
